import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import env from '@/core/config/env';

/**
 * Interface for queued requests during token refresh
 */
interface QueuedRequest {
    resolve: (value: unknown) => void;
    reject: (reason?: unknown) => void;
}

let isRefreshing = false;
let failedQueue: QueuedRequest[] = [];

const processQueue = (error: AxiosError | null, token: string | null = null) => {
    failedQueue.forEach((prom) => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

/**
 * Axios instance with base configuration targeting FastAPI backend
 */
const apiClient = axios.create({
    baseURL: env.API_URL,
    timeout: env.API_TIMEOUT,
    headers: {
        'Content-Type': 'application/json',
    },
});

/**
 * Request interceptor - Add auth token to requests
 */
apiClient.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
        const token = localStorage.getItem('auth_token');

        // Identify if route is protected (exclude logins, registration, public resources)
        const isPublicPath = 
            config.url?.includes('/auth/login') ||
            config.url?.includes('/auth/register') ||
            config.url?.includes('/auth/forgot-password') ||
            config.url?.includes('/auth/reset-password') ||
            config.url?.includes('/public') ||
            config.url === '/health';

        if (!isPublicPath && !token) {
            // Rejects request before making call to prevent protected requests after logout
            return Promise.reject(new Error('No auth token available. Please login first.'));
        }

        if (token && config.headers) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        return config;
    },
    (error: AxiosError) => {
        return Promise.reject(error);
    }
);

/**
 * Response interceptor - Handle common errors & token refresh
 */
apiClient.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

        const isAuthOrPublic = 
            originalRequest?.url?.includes('/auth/login') ||
            originalRequest?.url?.includes('/auth/register') ||
            originalRequest?.url?.includes('/auth/forgot-password') ||
            originalRequest?.url?.includes('/auth/reset-password') ||
            originalRequest?.url?.includes('/public');

        // Handle 401 Unauthorized (Expired Token)
        if (error.response?.status === 401 && !isAuthOrPublic && !originalRequest._retry) {
            const refreshToken = localStorage.getItem('refresh_token');

            if (!refreshToken) {
                localStorage.clear();
                sessionStorage.clear();
                window.location.href = '/login';
                return Promise.reject(error);
            }

            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                })
                    .then((token) => {
                        if (originalRequest.headers) {
                            originalRequest.headers.Authorization = `Bearer ${token}`;
                        }
                        return apiClient(originalRequest);
                    })
                    .catch((err) => Promise.reject(err));
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                // Silent token refresh targeting FastAPI backend endpoint
                const res = await axios.post(`${env.API_URL}/auth/refresh`, {
                    refresh_token: refreshToken,
                });

                const tokens = res.data.data || res.data;
                const access_token = tokens.access_token;
                const new_refresh_token = tokens.refresh_token;

                localStorage.setItem('auth_token', access_token);
                if (new_refresh_token) {
                    localStorage.setItem('refresh_token', new_refresh_token);
                }

                processQueue(null, access_token);

                if (originalRequest.headers) {
                    originalRequest.headers.Authorization = `Bearer ${access_token}`;
                }
                return apiClient(originalRequest);
            } catch (refreshError) {
                processQueue(refreshError as AxiosError, null);
                localStorage.clear();
                sessionStorage.clear();
                window.location.href = '/login';
                return Promise.reject(refreshError);
            } finally {
                isRefreshing = false;
            }
        }

        // Global Error Handling
        if (error.response?.status === 403) {
            console.error('Access forbidden: You do not have permissions for this action.');
        } else if (error.response?.status === 500) {
            console.error('Server Error: An unexpected internal error occurred on backend.');
        }

        return Promise.reject(error);
    }
);

export default apiClient;
